import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, BookOpen, Filter } from "lucide-react";

const TYPE_COLORS = {
  Asset:     "hsl(153, 34%, 27%)",
  Liability: "hsl(11, 67%, 62%)",
  Equity:    "hsl(252, 40%, 50%)",
  Revenue:   "hsl(193, 65%, 40%)",
  Expense:   "hsl(30, 43%, 64%)",
};

export default function ChartOfAccountsPage() {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    code: "", name: "", type: "Expense",
    description: "", tax_rate: "10", is_active: true,
  });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== "all") params.type = typeFilter;
      params.active_only = false;
      const res = await axios.get(`${API}/chart-of-accounts`, { headers, params });
      setAccounts(res.data);
    } catch {
      toast.error("Failed to fetch accounts");
    } finally { setLoading(false); }
  }, [headers, typeFilter]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const resetForm = () => {
    setEditing(null);
    setFormData({
      code: "", name: "", type: "Expense",
      description: "", tax_rate: "10", is_active: true,
    });
  };

  const handleEdit = (acc) => {
    setEditing(acc);
    setFormData({
      code: acc.code || "",
      name: acc.name || "",
      type: acc.type || "Expense",
      description: acc.description || "",
      tax_rate: acc.tax_rate != null ? String(acc.tax_rate) : "0",
      is_active: acc.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        type: formData.type,
        description: formData.description,
        tax_rate: parseFloat(formData.tax_rate) || 0,
        is_active: formData.is_active,
      };
      if (editing) {
        await axios.put(`${API}/chart-of-accounts/${editing.id}`, payload, { headers });
        toast.success("Account updated");
      } else {
        await axios.post(`${API}/chart-of-accounts`, payload, { headers });
        toast.success("Account created");
      }
      setDialogOpen(false);
      resetForm();
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save account");
    }
  };

  const handleDelete = async (acc) => {
    const verb = acc.is_system ? "disable" : "delete";
    if (!window.confirm(`Are you sure you want to ${verb} "${acc.name}"?`)) return;
    try {
      await axios.delete(`${API}/chart-of-accounts/${acc.id}`, { headers });
      toast.success(`Account ${verb}d`);
      fetchAccounts();
    } catch {
      toast.error("Failed");
    }
  };

  // Group accounts by type once per accounts change. Pre-compute the ordered, non-empty
  // type list for stable rendering (eliminates filter+map work in the JSX render path).
  const TYPE_ORDER = ["Revenue", "Expense", "Asset", "Liability", "Equity"];
  const grouped = useMemo(() => {
    return accounts.reduce((acc, a) => {
      (acc[a.type] = acc[a.type] || []).push(a);
      return acc;
    }, {});
  }, [accounts]);
  const visibleTypes = useMemo(
    () => TYPE_ORDER.filter((t) => grouped[t]?.length),
    // TYPE_ORDER is module-level constant
    [grouped]
  );

  return (
    <DashboardLayout title="Chart of Accounts">
      <div className="space-y-6" data-testid="coa-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]" data-testid="coa-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Asset">Assets</SelectItem>
                <SelectItem value="Liability">Liabilities</SelectItem>
                <SelectItem value="Equity">Equity</SelectItem>
                <SelectItem value="Revenue">Revenue</SelectItem>
                <SelectItem value="Expense">Expenses</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-hover" data-testid="add-account-btn">
                <Plus className="w-4 h-4 mr-2" />Add Account
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[520px]">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Account" : "Add New Account"}</DialogTitle>
                <DialogDescription>
                  {editing ? "Update the account" : "Create a new account in your chart of accounts"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Code *</Label>
                    <Input
                      data-testid="coa-code-input"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      placeholder="e.g., 485"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(v) => setFormData({ ...formData, type: v })}
                    >
                      <SelectTrigger data-testid="coa-type-input"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Asset">Asset</SelectItem>
                        <SelectItem value="Liability">Liability</SelectItem>
                        <SelectItem value="Equity">Equity</SelectItem>
                        <SelectItem value="Revenue">Revenue</SelectItem>
                        <SelectItem value="Expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    data-testid="coa-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Subscriptions"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Short description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 items-end">
                  <div className="space-y-2">
                    <Label>GST / Tax rate (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.tax_rate}
                      onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                      placeholder="10"
                    />
                  </div>
                  <div className="flex items-center gap-2 pb-1">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-input"
                    />
                    <Label htmlFor="is_active" className="cursor-pointer">Active</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="save-account-btn">
                    {editing ? "Update" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="section-title">
              Accounts ({accounts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-muted-foreground">Loading accounts...</div>
              </div>
            ) : accounts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center p-6">
                <BookOpen className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No accounts</h3>
                <p className="text-muted-foreground mt-1">Add your first account to get started</p>
              </div>
            ) : (
              <div className="space-y-6 p-4">
                {visibleTypes.map((t) => (
                  <div key={t}>
                    <div className="flex items-center gap-2 mb-2 px-2">
                      <Badge style={{ backgroundColor: TYPE_COLORS[t] + "20", color: TYPE_COLORS[t] }}>
                        {t}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {grouped[t].length} account{grouped[t].length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="table-header w-24">Code</TableHead>
                          <TableHead className="table-header">Name</TableHead>
                          <TableHead className="table-header">Description</TableHead>
                          <TableHead className="table-header text-right w-24">GST%</TableHead>
                          <TableHead className="table-header w-28">Status</TableHead>
                          <TableHead className="table-header text-right w-28">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {grouped[t].map((a) => (
                          <TableRow key={a.id} className="hover:bg-accent/50 transition-colors" data-testid={`coa-row-${a.code}`}>
                            <TableCell className="font-mono text-sm">{a.code}</TableCell>
                            <TableCell className="font-medium">
                              {a.name}
                              {a.is_system && (
                                <Badge variant="outline" className="ml-2 text-xs">System</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">{a.description}</TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {a.tax_rate != null ? `${a.tax_rate}%` : "-"}
                            </TableCell>
                            <TableCell>
                              {a.is_active
                                ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                                : <Badge variant="outline" className="bg-gray-50 text-gray-500">Inactive</Badge>}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => handleEdit(a)} data-testid={`edit-account-${a.code}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost" size="icon"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => handleDelete(a)}
                                  data-testid={`delete-account-${a.code}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
